import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['tab', 'panel', 'toggle', 'resettable']

  // The visible panel is the single source of truth: it survives redraws that
  // replace the tabs (the calendar drawer's panels are turbo-permanent, the
  // tab rail is not).
  get activeIndex() {
    return this.panelTargets.findIndex(panel => !panel.classList.contains('hidden'))
  }

  dispatchToggleChange() {
    this.toggleTarget.dispatchEvent(new Event('change', { bubbles: true }))
  }


  close() {
    this.toggleTarget.checked = false
    this.dispatchToggleChange()
  }

  // A frame that must not outlive the drawer state that opened it. Clearing
  // src as well as content means it reloads from the top next time rather than
  // restoring the step it was left on.
  resetResettables() {
    this.resettableTargets.forEach(frame => {
      frame.removeAttribute('src')
      frame.innerHTML = ''
    })
  }

  selectTab(event) {
    this.resetResettables()

    const tab = event.currentTarget
    const index = this.tabTargets.indexOf(tab)

    if (this.hasToggleTarget && index === this.activeIndex && this.toggleTarget.checked) {
      this.toggleTarget.checked = false
      this.dispatchToggleChange()
      tab.setAttribute('data-active', 'false')
      return
    }

    this.showPanel(index)

    if (this.hasToggleTarget) {
      this.toggleTarget.checked = true
      this.dispatchToggleChange()
    }
  }

  showPanel(index) {
    this.panelTargets.forEach((panel, i) => {
      if (i === index) {
        panel.classList.remove('hidden')
      } else {
        panel.classList.add('hidden')
      }
    })

    this.tabTargets.forEach((tab, i) => {
      this.setTabState(tab, i === index)
    })
  }

  toggleTargetConnected(toggle) {
    // Named and removed on disconnect: the toggle can be turbo-permanent, so
    // an anonymous listener would stack a stale closure per page visit.
    this.onToggleChange = () => {
      if (!toggle.checked) {
        this.tabTargets.forEach(tab => this.setTabState(tab, false))
        this.resetResettables()
      }
    }
    toggle.addEventListener('change', this.onToggleChange)
    // Hooked here rather than connect: the toggle is turbo-permanent and lands
    // after the initial connect, once Turbo swaps the real elements in.
    if (toggle.checked) {
      this.tabTargets.forEach((tab, i) => this.setTabState(tab, i === this.activeIndex))
    }
  }

  toggleTargetDisconnected(toggle) {
    toggle.removeEventListener('change', this.onToggleChange)
  }

  // aria-selected is only valid on role="tab" (the drawer tablist); plain
  // toggle buttons (ui/tab_bar_item) convey state with aria-pressed instead.
  setTabState(tab, isActive) {
    const ariaAttribute = tab.getAttribute('role') === 'tab' ? 'aria-selected' : 'aria-pressed'
    tab.setAttribute('data-active', isActive ? 'true' : 'false')
    tab.setAttribute(ariaAttribute, isActive ? 'true' : 'false')
  }
}

